import { StyledOrderItems, StyledMobileEditList, EditableProductName } from './style';
import { useIntl } from 'react-intl';
import {
  ItemProps,
  DeliveryCondition,
} from '../../../../../interfaces/BackendModels';
import MobileList from 'admin/components/MobileLayout/MobileList';
import { ResponsiveWrapper } from 'admin/components/ResponsiveWrapper';
import { Form, Button } from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import { InputNumber } from 'ui/InputNumber';
import { CloseOutlined } from '@ant-design/icons';
import { getUnitShort } from '../../../../../utils/unitMapping';
import { FallbackImage } from 'ui/UploadImage/fallBack/FallBackImage';
import { AddProductModal } from './AddProductModal';
import { useState } from 'react';

interface OrderItemsTableProps {
  products: ItemProps[];
  isEditMode?: boolean;
  farmId?: number;
  deliveryConditions?: DeliveryCondition[];
  transactionStatus?: string;
  showPvn?: boolean;
}

interface MergedItem extends ItemProps {
  merged_count: number;
}

const mergeProductsByProductId = (items: ItemProps[]): MergedItem[] => {
  const merged = new Map<number, MergedItem>();

  items.forEach((item) => {
    const productId = item.product_id;
    if (merged.has(productId)) {
      const existing = merged.get(productId)!;
      existing.stock_count = String(
        Number(existing.stock_count) + Number(item.stock_count)
      );
      existing.stock_total = String(
        Number(existing.stock_total) + Number(item.stock_total)
      );
      existing.merged_count += 1;
    } else {
      merged.set(productId, { ...item, merged_count: 1 });
    }
  });

  return Array.from(merged.values());
};

export const OrderItemsTable = ({
  products,
  isEditMode = false,
  farmId,
  deliveryConditions,
  transactionStatus,
  showPvn,
}: OrderItemsTableProps) => {
  const intl = useIntl();
  const form = useFormInstance();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<{
    item: ItemProps;
    index: number;
  } | null>(null);

  const isDeliveryManagementMode = [
    'pending_send',
    'partially_sent',
    'sent',
  ].includes(transactionStatus?.toLowerCase() ?? '');

  const getAssignedQuantityForProduct = (productId: number): number => {
    if (!deliveryConditions) return 0;
    const productItemIds = products
      .filter((i) => i.product_id === productId)
      .map((i) => i.id);
    let assigned = 0;
    deliveryConditions.forEach((dc) => {
      dc.fulfillments?.forEach((f) => {
        f.products?.forEach((p) => {
          const orderItemId = p.id ?? p.order_item_id;
          if (
            orderItemId !== undefined &&
            productItemIds.includes(orderItemId)
          ) {
            assigned += Number(
              p.pivot?.product_quantity ?? p.product_quantity ?? 0
            );
          }
        });
      });
    });
    return assigned;
  };

  const getProductsWithRemainingQty = (items: MergedItem[]): MergedItem[] => {
    if (!isDeliveryManagementMode) return items;

    return items
      .map((item) => {
        const assigned = getAssignedQuantityForProduct(item.product_id);
        const remaining = Number(item.stock_count) - assigned;
        return {
          ...item,
          stock_count: String(Math.max(0, remaining)),
          stock_total: String(
            Math.max(0, remaining) * Number(item.stock_price)
          ),
        };
      })
      .filter((item) => Number(item.stock_count) > 0);
  };

  const handleValueChange = (index: number, field: string, value: number) => {
    const items = form.getFieldValue('items') || [];
    items[index][field] = value;

    if (field === 'stock_price' || field === 'stock_count') {
      items[index].stock_total =
        items[index].stock_price * items[index].stock_count;
    }

    form.setFieldsValue({ items });
  };

  const handleEditRow = (item: ItemProps, index: number) => {
    setEditingItem({ item, index });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  const handleAddProduct = (
    productData: {
      product_id: number;
      product_name: string;
      stock_price: number;
      stock_count: number;
      stock_unit: string;
      stock_total: number;
      product_category_id: number;
      product_subcategory_id: number;
      vat: number;
    },
    add?: (defaultValue?: Partial<ItemProps>, insertIndex?: number) => void,
    remove?: (index: number | number[]) => void
  ) => {
    const itemData: Partial<ItemProps> = {
      product_id: productData.product_id,
      product_name: productData.product_name,
      stock_price: String(productData.stock_price),
      stock_count: String(productData.stock_count),
      stock_unit: productData.stock_unit,
      stock_total: String(productData.stock_total),
      product_category_id: productData.product_category_id,
      product_subcategory_id: productData.product_subcategory_id,
      vat: productData.vat,
    };

    if (editingItem !== null && remove && add) {
      remove(editingItem.index);
      add(itemData, editingItem.index);
    } else if (add) {
      add(itemData);
    }
  };

  const watchedItems = Form.useWatch('items', form);
  const rawDataSource = watchedItems || products;
  const mergedProducts = mergeProductsByProductId(rawDataSource);
  const dataSource = isEditMode
    ? rawDataSource
    : getProductsWithRemainingQty(mergedProducts);

  const getColumns = (remove?: (index: number | number[]) => void) => [
    {
      title: intl.formatMessage({ id: 'general.product' }),
      dataIndex: 'product_name',
      render: (value: string, record: ItemProps, index: number) => {
        const qualityMarks = record.product?.qualitymarks?.filter(
          (q, i, arr) =>
            arr.findIndex((x) => x.quality_mark_id === q.quality_mark_id) === i
        );

        return (
          <div className="product-cell">
            {isEditMode ? (
              <EditableProductName onClick={() => handleEditRow(record, index)}>
                {value || '-'}
              </EditableProductName>
            ) : (
              <span>{value || '-'}</span>
            )}
            {qualityMarks && qualityMarks.length > 0 && (
              <div className="quality-marks">
                {qualityMarks.map((qmark) => (
                  <FallbackImage
                    key={qmark.quality_mark_id}
                    className="quality-mark"
                    src={qmark.quality_mark?.logo || ''}
                    type="notFound"
                    preview={false}
                  />
                ))}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: intl.formatMessage({ id: showPvn !== false ? 'general.price' : 'general.price_no_pvn' }),
      dataIndex: 'stock_price',
      render: (value: number, record: ItemProps, index: number) => {
        if (!isEditMode) {
          return (
            <>
              {Number(value ?? '').toFixed(2)} € /{record.stock_unit}
            </>
          );
        }
        return (
          <InputNumber
            name={[index, 'stock_price']}
            min={0}
            controls={false}
            onChange={(val) =>
              handleValueChange(index, 'stock_price', Number(val) || 0)
            }
            addonAfter={getUnitShort(record.stock_unit)}
          />
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'general.amount_table' }),
      dataIndex: 'stock_count',
      render: (value: number, record: ItemProps, index: number) => {
        if (!isEditMode) {
          return <>x {value}</>;
        }
        return (
          <InputNumber
            name={[index, 'stock_count']}
            min={0.01}
            step={1}
            onChange={(val) =>
              handleValueChange(index, 'stock_count', Number(val) || 0.01)
            }
          />
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'general.total' }),
      dataIndex: 'stock_total',
      render: (value: number) => {
        return <>€ {Number(value ?? '').toFixed(2)}</>;
      },
    },
    ...(isEditMode && remove
      ? [
          {
            title: '',
            key: 'actions',
            render: (_: ItemProps, __: ItemProps, index: number) => (
              <Button
                icon={<CloseOutlined />}
                onClick={() => remove(index)}
                size="small"
                className="delete-item-btn"
              />
            ),
          },
        ]
      : []),
  ];

  const mobileItems = {
    bodyFields: [
      {
        label: intl.formatMessage({ id: 'general.product' }),
        name: 'product_name',
        render: (value: string) => value || '-',
      },
      {
        label: intl.formatMessage({ id: showPvn !== false ? 'general.price' : 'general.price_no_pvn' }),
        name: 'stock_price',
        render: (value: number, record: ItemProps) => {
          return (
            <>
              {Number(value ?? '').toFixed(2)} € /{record.stock_unit}
            </>
          );
        },
      },
      {
        label: intl.formatMessage({ id: 'general.amount_table' }),
        name: 'stock_count',
        render: (value: number) => {
          return <>x {value}</>;
        },
      },
      {
        label: intl.formatMessage({ id: 'general.total' }),
        name: 'stock_total',
        render: (value: number) => {
          return <>€ {Number(value ?? '').toFixed(2)}</>;
        },
      },
    ],
  };

  return (
    <Form.List name="items">
      {(fields, { add, remove }) => (
        <>
          {isEditMode ? (
            <ResponsiveWrapper
              desktopComponent={
                <StyledOrderItems
                  columns={getColumns(remove)}
                  dataSource={dataSource}
                  footer={
                    <Button
                      onClick={() => setIsModalOpen(true)}
                      className="add-product-btn"
                    >
                      {intl.formatMessage({ id: 'general.add_product' })}
                    </Button>
                  }
                />
              }
              mobileComponent={
                <StyledMobileEditList>
                  {dataSource.map((item: ItemProps, index: number) => (
                    <div className="edit-card" key={index}>
                      <div className="edit-card-row">
                        <div className="edit-card-label-value">
                          <span className="label">
                            {intl.formatMessage({ id: 'general.product' })}
                          </span>
                          <EditableProductName onClick={() => handleEditRow(item, index)}>
                            {item.product_name || '-'}
                          </EditableProductName>
                        </div>
                        <div className="edit-card-label-value end">
                          <span className="label">
                            {intl.formatMessage({ id: showPvn !== false ? 'general.price' : 'general.price_no_pvn' })}
                          </span>
                          <span>{Number(item.stock_price ?? 0).toFixed(2)} € /{item.stock_unit}</span>
                        </div>
                      </div>
                      <div className="edit-card-row">
                        <div className="edit-card-label-value">
                          <span className="label">
                            {intl.formatMessage({ id: showPvn !== false ? 'general.price' : 'general.price_no_pvn' })}
                          </span>
                          <InputNumber
                            name={[index, 'stock_price']}
                            min={0}
                            controls={false}
                            onChange={(val) => handleValueChange(index, 'stock_price', Number(val) || 0)}
                            addonAfter={getUnitShort(item.stock_unit)}
                          />
                        </div>
                        <div className="edit-card-label-value">
                          <span className="label">
                            {intl.formatMessage({ id: 'general.amount_table' })}
                          </span>
                          <InputNumber
                            name={[index, 'stock_count']}
                            min={0.01}
                            step={1}
                            onChange={(val) => handleValueChange(index, 'stock_count', Number(val) || 0.01)}
                          />
                        </div>
                      </div>
                      <div className="edit-card-row">
                        <Button
                          onClick={() => remove(index)}
                          className="delete-item-btn"
                        >
                          {intl.formatMessage({ id: 'general.delete' })}
                        </Button>
                        <div className="edit-card-label-value end">
                          <span className="label">
                            {intl.formatMessage({ id: 'general.total' })}
                          </span>
                          <span className="total">€ {Number(item.stock_total ?? 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button
                    onClick={() => setIsModalOpen(true)}
                    className="add-product-btn"
                  >
                    {intl.formatMessage({ id: 'general.add_product' })}
                  </Button>
                </StyledMobileEditList>
              }
            />
          ) : (
            <ResponsiveWrapper
              desktopComponent={
                <StyledOrderItems
                  columns={getColumns(remove)}
                  dataSource={dataSource}
                />
              }
              mobileComponent={
                <MobileList
                  coverFields={undefined}
                  bodyFields={mobileItems.bodyFields}
                  dataSource={dataSource}
                />
              }
            />
          )}
          <AddProductModal
            open={isModalOpen}
            onClose={handleCloseModal}
            onAdd={(productData) => handleAddProduct(productData, add, remove)}
            farmId={farmId}
            editItem={editingItem?.item}
            editIndex={editingItem?.index}
          />
        </>
      )}
    </Form.List>
  );
};
